import { useState, FormEvent } from 'react';

const ZAPIER_WEBHOOK = 'https://hooks.zapier.com/hooks/catch/26282998/un33dif/';

export default function HeroForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const zapierBody = new URLSearchParams();
      zapierBody.append('full_name', formData.name);
      zapierBody.append('email', formData.email);
      zapierBody.append('phone', formData.phone);
      zapierBody.append('company_firm', formData.company);
      zapierBody.append('project_type', formData.projectType);
      zapierBody.append('timeline', formData.timeline);
      zapierBody.append('product_details', formData.message);

      const [response] = await Promise.all([
        fetch('https://readdy.ai/api/form/d6b94k6a728k8ctu3p4g', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        fetch(ZAPIER_WEBHOOK, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: zapierBody.toString(),
        }).catch(() => null),
      ]);

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', projectType: '', timeline: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg focus:ring-1 focus:ring-[#C5A572] focus:border-[#C5A572] outline-none transition-all text-xs text-white placeholder-white/40 backdrop-blur-sm';

  const labelClass = 'block text-xs font-medium text-white/70 mb-1';

  return (
    <div className="bg-black/30 backdrop-blur-xl border border-white/15 rounded-2xl p-7 w-full max-w-lg">
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-1 h-5 bg-[#C5A572] rounded-full"></div>
          <h3 className="text-base font-semibold text-white tracking-wide">Start Your Inquiry</h3>
        </div>
        <p className="text-white/45 text-xs ml-3">We&apos;ll respond within 24 hours</p>
      </div>

      <form id="hero-inquiry-form" onSubmit={handleSubmit} data-readdy-form>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label htmlFor="h-name" className={labelClass}>
              Full Name <span className="text-[#C5A572]">*</span>
            </label>
            <input
              type="text"
              id="h-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className={inputClass}
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="h-email" className={labelClass}>
              Email <span className="text-[#C5A572]">*</span>
            </label>
            <input
              type="email"
              id="h-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={inputClass}
              placeholder="your@email.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label htmlFor="h-phone" className={labelClass}>
              Phone <span className="text-[#C5A572]">*</span>
            </label>
            <input
              type="tel"
              id="h-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className={inputClass}
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label htmlFor="h-company" className={labelClass}>
              Company / Firm
            </label>
            <input
              type="text"
              id="h-company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className={inputClass}
              placeholder="Your design firm"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label htmlFor="h-projectType" className={labelClass}>
              Project Type
            </label>
            <select
              id="h-projectType"
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              className={`${inputClass} cursor-pointer`}
            >
              <option value="" className="bg-[#1a1a1a] text-white">Select type</option>
              <option value="residential" className="bg-[#1a1a1a] text-white">Residential</option>
              <option value="commercial" className="bg-[#1a1a1a] text-white">Commercial</option>
              <option value="hospitality" className="bg-[#1a1a1a] text-white">Hospitality</option>
              <option value="retail" className="bg-[#1a1a1a] text-white">Retail</option>
              <option value="other" className="bg-[#1a1a1a] text-white">Other</option>
            </select>
          </div>
          <div>
            <label htmlFor="h-timeline" className={labelClass}>
              Timeline
            </label>
            <select
              id="h-timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              className={`${inputClass} cursor-pointer`}
            >
              <option value="" className="bg-[#1a1a1a] text-white">Select timeline</option>
              <option value="immediate" className="bg-[#1a1a1a] text-white">1-2 weeks</option>
              <option value="1-month" className="bg-[#1a1a1a] text-white">Within 1 month</option>
              <option value="1-3-months" className="bg-[#1a1a1a] text-white">1-3 months</option>
              <option value="3-6-months" className="bg-[#1a1a1a] text-white">3-6 months</option>
              <option value="flexible" className="bg-[#1a1a1a] text-white">Flexible</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="h-message" className={labelClass}>
            Project Details
          </label>
          <textarea
            id="h-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            maxLength={500}
            rows={3}
            className={`${inputClass} resize-none`}
            placeholder="Tell us about your project..."
          ></textarea>
          <div className="text-right text-xs text-white/30 mt-0.5">
            {formData.message.length}/500
          </div>
        </div>

        {submitStatus === 'success' && (
          <div className="mb-3 p-3 bg-[#C5A572]/20 border border-[#C5A572]/40 rounded-lg text-[#C5A572] text-xs">
            <i className="ri-checkbox-circle-line mr-1"></i>
            Thank you! We&apos;ll be in touch within 24 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-3 p-3 bg-red-500/10 border border-red-400/30 rounded-lg text-red-300 text-xs">
            <i className="ri-error-warning-line mr-1"></i>
            Please fill in all required fields (name, email &amp; phone).
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#C5A572] text-white px-6 py-3 rounded-full text-xs font-semibold hover:bg-[#b8956a] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed tracking-wider uppercase"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
        </button>
      </form>
    </div>
  );
}
